import { createSelector } from 'reselect'
import {
  getAlgoliaDataHitsPerPage,
  getAlgoliaDataPaginationCurrent,
  getAlgoliaDataHitsResults,
  getAlgoliaDataHitsTotal,
  getAlgoliaDataHits,
} from 'selectors/algolia2/data'
import { getPage } from 'selectors/algolia2/url'
import { getSelectedFacetNameFirstAttribute } from 'selectors/algolia2/facets'
import { getPageData, getPageParams, getPagePath } from 'selectors/page'
import { slugify } from 'lib/slugify'
import { splitAndCapitalizeString, urlQuery } from 'lib/helpers'
import { getFirstThreeArtists } from '../helpers'
export { getPageData, getPageParams, getPagePath }
export { getAlgoliaUserToken } from 'selectors/user/cookie'
export const getHitsIsSeoValidCount = createSelector(
  [getAlgoliaDataHitsResults],
  (results): number => results.reduce((count, { isSeoValid }) => (count += isSeoValid ? 1 : 0), 0)
)
export const getFeatureHitsPerPage = createSelector([getPageParams], ({ hitsPerPage }): number => {
  return Number(hitsPerPage) || 50
})
export const getHitsAvailableForSaleCount = createSelector(
  [getAlgoliaDataHitsResults],
  (results): number =>
    results.reduce((count, { originalStatus }) => (count += originalStatus === 'avail' ? 1 : 0), 0)
)
export const getCategory = createSelector(
  getSelectedFacetNameFirstAttribute('category'),
  (category): string => category || ''
)
export const getKeyword = createSelector(
  [getPageParams],
  (pageParams): string => pageParams.keyword || ''
)
export const getUrlPathString = createSelector(
  [getCategory, getKeyword],
  (category, keyword): string => `/${slugify(category)}/${slugify(keyword)}/feature`
)
export const getAlgoliaQuery = createSelector(
  [getAlgoliaDataHitsPerPage, getAlgoliaDataPaginationCurrent],
  (hitsPerPage, page): Record<string, any> => ({
    ...(hitsPerPage !== 50
      ? {
          hitsPerPage,
        }
      : {}),
    ...(page !== 0
      ? {
          page: page + 1,
        }
      : {}),
  })
)
export const getUrlQueryString = createSelector([getAlgoliaQuery], (algoliaQueryParams): string =>
  urlQuery(algoliaQueryParams)
)
export const getUrlString = createSelector(
  [getUrlPathString, getUrlQueryString],
  (urlPathString, urlQueryString): string => `${urlPathString}${urlQueryString}`
)
export const createSearchUrl = (search: string) =>
  createSelector(
    [getCategory, getKeyword, getUrlQueryString],
    (category, keyword, urlQueryString): string =>
      `/${slugify(category)}/${slugify(search || keyword)}/feature${urlQueryString}`
  )
export const getDataLayer = createSelector(
  [getCategory, getKeyword, getPage, getKeyword],
  (category, keyword, pagination, sort): Record<string, any> => ({
    category,
    event: 'pagetype',
    keyword,
    pagetype: 'browse',
    pagination,
    subpagetype: 'feature',
  })
)
export const getTitle = createSelector(
  [getKeyword],
  (keyword): string => `${splitAndCapitalizeString(keyword)}`
)
export const getSeoCategory = createSelector([getCategory], (category): string => {
  const cleanCategory =
    category === ('Paintings' || 'All')
      ? category.replace(/^[All]$/gi, 'Artworks')
      : `${category} Artworks`
  return cleanCategory || ''
})
export const getDescription = createSelector(
  [getSeoCategory, getTitle, getAlgoliaDataHitsTotal, getAlgoliaDataHits],
  (category, title, totalHits, algoliaHits): string => {
    const { results } = algoliaHits
    const artists = getFirstThreeArtists(results)
    return `Results for "${title}" ${category}. Discover ${totalHits} ${category} by top emerging artists from around the world, including ${artists[0]}, ${artists[1]}, and ${artists[2]}. Buy original art worry free with our 14-day satisfaction guarantee.`
  }
)
export const getKeywords = createSelector(
  [getCategory, getKeyword],
  (category, keyword): string =>
    `${keyword}, ${category}, original art, art, artworks, saatchi art, saatchi, emerging art, prints, art print`
)
export const getBreadcrumbsPaths = createSelector(
  [getCategory, getKeyword],
  (category, keyword): Array<string> => [
    `/${slugify(category)}`,
    `/${slugify(category)}/${slugify(keyword)}/feature`,
  ]
)
export const getMetaData = createSelector(
  [
    getCategory,
    getDescription,
    getTitle,
    getHitsIsSeoValidCount,
    getHitsAvailableForSaleCount,
    getAlgoliaDataHitsTotal,
  ],
  (
    category,
    description,
    metaTitle,
    isSeoValidCount,
    availableForSaleCount,
    hitsTotal
  ): Record<string, any> => {
    const title = `${metaTitle} ${category} | Saatchi Art`
    const noFollow = hitsTotal < 10 || isSeoValidCount < 10 || availableForSaleCount < 1
    return {
      description,
      title,
      og: {
        description,
        title,
        type: 'product',
      },
      preloadImages: [],
      robots: !noFollow,
      schema: {
        webpage: {
          headline: title,
          description,
        },
      },
    }
  }
)